import React from "react";
import { useNavigate } from "react-router-dom";
import { SearchIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { adminSidebarData } from "./data/admin-sidebar-data";

interface FlatRoute {
  title: string;
  url: string;
  group: string;
  parent?: string;
}

function flattenRoutes(): FlatRoute[] {
  const routes: FlatRoute[] = [];
  for (const group of adminSidebarData.navGroups) {
    for (const item of group.items) {
      if ("url" in item && item.url) {
        routes.push({
          title: item.title,
          url: item.url as string,
          group: group.title,
        });
      } else if ("items" in item && item.items) {
        for (const sub of item.items) {
          if ("url" in sub && sub.url) {
            routes.push({
              title: sub.title,
              url: sub.url as string,
              group: group.title,
              parent: item.title,
            });
          }
        }
      }
    }
  }
  return routes;
}

const allRoutes = flattenRoutes();

const groupedRoutes = allRoutes.reduce<Record<string, FlatRoute[]>>(
  (acc, route) => {
    const key = route.parent ? `${route.group} › ${route.parent}` : route.group;
    if (!acc[key]) acc[key] = [];
    acc[key].push(route);
    return acc;
  },
  {},
);

export function RouteSearch() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleSelect = (url: string) => {
    setOpen(false);
    navigate(url);
  };

  return (
    <>
      {/* Icon-only on mobile, full pill on sm+ */}
      <Button
        variant="outline"
        size="icon"
        title="Search routes (⌘K)"
        className="sm:hidden"
        onClick={() => setOpen(true)}
      >
        <SearchIcon className="size-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="text-muted-foreground hidden h-8 w-56 justify-start gap-2 text-sm font-normal sm:flex"
        onClick={() => setOpen(true)}
      >
        <SearchIcon className="size-4" />
        <span>Search routes...</span>
        <kbd className="bg-muted pointer-events-none ml-auto hidden h-5 select-none items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium sm:flex">
          <span>⌘K</span>
        </kbd>
      </Button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Route Search"
        description="Search for a route"
      >
        <CommandInput placeholder="Search routes..." />
        <CommandList>
          <CommandEmpty>No routes found.</CommandEmpty>
          {Object.entries(groupedRoutes).map(([group, routes]) => (
            <CommandGroup key={group} heading={group}>
              {routes.map((route) => (
                <CommandItem
                  key={route.url + route.title}
                  value={`${route.title} ${route.parent ?? ""} ${group}`}
                  onSelect={() => handleSelect(route.url)}
                >
                  {route.title}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
